import React, { useState, useEffect } from 'react';
import { Card, Button, Input, Typography, List, Tag, message, Spin, Avatar, Statistic, Row, Col, InputNumber } from 'antd';
import { StarOutlined, CheckCircleOutlined, CloseCircleOutlined, SearchOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface AirdropProject {
  id: string;
  name: string;
  icon: string;
  description: string;
  status: string;
  unit: string; // 代币单位
}

interface QueryResult {
  address: string;
  eligible: boolean;
  amount?: string;
  error?: string;
}

interface ProjectQueryResult {
  eligible: boolean;
  amount?: string;
}

const airdropProjects: AirdropProject[] = [
  {
    id: 'spark',
    name: 'Spark',
    icon: 'https://pbs.twimg.com/profile_images/1856332015341084672/lF5ZZXRm_400x400.jpg',
    description: 'Spark Airdrop eligibility Checker 官网查询地址: https://app.spark.fi/SPK/airdrop',
    status: '未公布',
    unit: 'SPK'
  },
  {
    id: 'jager',
    name: 'Jager',
    icon: 'https://jager.meme/_next/image?url=%2Fimages%2Fhome%2Fhunter.png&w=3840&q=75',
    description: 'Jager 空投钱包批量查询',
    status: '未公布',
    unit: 'JAGER'
  },
  {
    id: 'haedal',
    name: 'Haedal',
    icon: 'https://pbs.twimg.com/profile_images/1680859397235539968/d0jiNiGg_400x400.jpg',
    description: 'Haedal 协议空投钱包批量查询',
    status: '未公布',
    unit: 'HAEDAL'
  },
  {
    id: 'stakestone',
    name: 'Stakestone',
    icon: 'https://pbs.twimg.com/profile_images/1665354784000589824/2nq_Ae8e_400x400.jpg',
    description: 'Stakestone 空投钱包批量查询',
    status: '03/02 02:00 - 05/04 02:00',
    unit: 'STONE'
  },
  {
    id: 'zora',
    name: 'ZORA',
    icon: 'https://pbs.twimg.com/profile_images/1912995896226443264/R9N6BIXd_400x400.jpg',
    description: 'ZORA 空投钱包批量查询',
    status: '未公布',
    unit: 'ZORA'
  }
];

// Spark 查询函数
const querySpark = async (address: string): Promise<ProjectQueryResult> => {
  try {
    const response = await fetch(`https://query.dddd-cors.workers.dev/?https://spark2-api.blockanalitica.com/api/v1/spk-airdrop/${address}/`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    
    // 新格式：返回数组，每个元素包含空投信息
    if (Array.isArray(data) && data.length > 0) {
      // 查找匹配的地址记录
      const record = data.find(item => 
        item.wallet_address?.toLowerCase() === address.toLowerCase()
      );
      
      if (record && record.amount_normalized) {
        // 格式化 amount_normalized，移除多余的小数点
        const amount = parseFloat(record.amount_normalized).toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 4
        });
        
        return {
          eligible: true,
          amount: amount
        };
      }
    }
    
    // 如果数组为空或没有找到匹配记录，则不符合条件
    return { eligible: false };
    
  } catch (error) {
    throw error;
  }
};

// Jager 查询函数
const queryJager = async (address: string): Promise<ProjectQueryResult> => {
  try {
    const response = await fetch(`https://api.jager.meme/api/airdrop/queryAirdrop/${address}/`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    return {
      eligible: data.eligible || false,
      amount: data.amount ? `${data.amount}` : undefined
    };
  } catch (error) {
    throw error;
  }
};

// Haedal 批量查询函数
const queryHaedal = async (addresses: string[]): Promise<Map<string, ProjectQueryResult>> => {
  const results = new Map<string, ProjectQueryResult>();
  
  try {
    const response = await fetch('https://web3.okx.com/priapi/v2/wallet/airdrop/tokenCanGet', {
      method: 'POST',
      headers: {
        "accept": "*/*, application/json",
        "content-type": "application/json",
      },
      body: JSON.stringify({
        queryId: addresses,
        projId: "3"
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    
    if (data.code === 0 && data.data && Array.isArray(data.data)) {
      // 处理返回的数据
      addresses.forEach(address => {
        const result = data.data.find((item: any) => 
          item.address?.toLowerCase() === address.toLowerCase()
        );
        
        if (result && result.data) {
          results.set(address, {
            eligible: result.data.canGet || false,
            amount: result.data.tokenAmount ? `${result.data.tokenAmount}` : undefined
          });
        } else {
          results.set(address, { eligible: false });
        }
      });
    } else {
      // 如果请求失败，所有地址标记为查询失败
      addresses.forEach(address => {
        results.set(address, { eligible: false });
      });
    }
  } catch (error) {
    // 网络错误，所有地址标记为查询失败
    addresses.forEach(address => {
      results.set(address, { eligible: false });
    });
    throw error;
  }

  return results;
};

// Stakestone 查询函数
const queryStakestone = async (address: string): Promise<ProjectQueryResult> => {
  try {
    const response = await fetch(`https://query.dddd-cors.workers.dev/?https://claims.stakestone.io/api/credentials?walletAddress=${address}&batchId=0`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    const data = await response.json();
    
    // 根据实际返回格式解析：{"tokenQualified":0,"proof":[]}
    // tokenQualified > 0 且 proof 不为空表示符合条件
    const isEligible = data.tokenQualified > 0 && Array.isArray(data.proof) && data.proof.length > 0;
    
    return {
      eligible: isEligible,
      amount: isEligible && data.tokenQualified ? `${data.tokenQualified}` : undefined
    };
  } catch (error) {
    throw error;
  }
};

// ZORA 查询函数
const queryZora = async (address: string): Promise<ProjectQueryResult> => {
  try {
    const response = await fetch('https://api.mct.club/ws/api/evm_call/multi_call/batch_address', {
      method: 'POST',
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
      },
      body: JSON.stringify({
        "rpc": "8453",
        "multicall": "",
        "contract": "0x0000000002ba96C69b95E32CAAB8fc38bAB8B3F8",
        "method": "allocations(address)(uint256)",
        "args": [address]
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    
    if (data.code === 0 && data.data) {
      // 从 data 对象中获取对应地址的值
      const value = typeof data.data === 'object' && !Array.isArray(data.data) 
        ? data.data[address.toLowerCase()] || data.data[address]
        : data.data;
      
      if (value && value !== '0') {
        // 转换大数为可读格式
        const num = BigInt(value);
        const decimals = BigInt(10 ** 18);
        const result = num / decimals;
        const remainder = num % decimals;
        const fractionStr = remainder.toString().padStart(18, '0').replace(/0+$/, '');
        const finalResult = fractionStr ? `${result}.${fractionStr}` : result.toString();
        
        // 限制小数位数
        const numResult = parseFloat(finalResult);
        const formattedAmount = numResult.toLocaleString('en-US', { 
          minimumFractionDigits: 0,
          maximumFractionDigits: 4 
        });
        
        return {
          eligible: true,
          amount: formattedAmount
        };
      }
    }
    
    return { eligible: false };
  } catch (error) {
    throw error;
  }
};

const AirdropWhitelist: React.FC = () => {
  const { projectId } = useParams<{ projectId?: string }>();
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState<AirdropProject | null>(null);
  const [addresses, setAddresses] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<QueryResult[]>([]);
  const [queryTime, setQueryTime] = useState<number>(0);
  const [queryDelay, setQueryDelay] = useState<number>(1); // 查询延迟间隔，默认1秒

  // 根据URL参数自动选择项目
  useEffect(() => {
    if (projectId) {
      const project = airdropProjects.find(p => p.id === projectId);
      if (project) {
        setSelectedProject(project);
      } else {
        // 如果项目ID不存在，重定向到项目列表
        navigate('/query/airdrop-whitelist', { replace: true });
        message.error('项目不存在');
      }
    } else {
      setSelectedProject(null);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    if (loading) {
      const timer = setInterval(() => {
        setQueryTime(prev => prev + 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [loading]);

  const handleProjectSelect = (project: AirdropProject) => {
    // 导航到项目特定的URL
    navigate(`/query/airdrop-whitelist/${project.id}`);
    setResults([]);
    setQueryTime(0);
  };

  const handleBackToList = () => {
    // 导航回项目列表
    navigate('/query/airdrop-whitelist');
    setAddresses('');
    setResults([]);
    setQueryTime(0);
  };

  const validateAddress = (address: string): boolean => {
    // 基本的以太坊地址验证
    return /^0x[a-fA-F0-9]{40}$/.test(address.trim());
  };

  const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

  const queryAirdrop = async () => {
    if (!selectedProject || !addresses.trim()) {
      message.warning('请输入钱包地址');
      return;
    }

    const addressList = addresses
      .split('\n')
      .map(addr => addr.trim())
      .filter(addr => addr.length > 0);

    if (addressList.length === 0) {
      message.warning('请输入有效的钱包地址');
      return;
    }

    // 验证地址格式
    const invalidAddresses = addressList.filter(addr => !validateAddress(addr));
    if (invalidAddresses.length > 0) {
      message.error(`以下地址格式不正确: ${invalidAddresses.join(', ')}`);
      return;
    }

    setLoading(true);
    setQueryTime(0);
    const newResults: QueryResult[] = [];

    try {
      if (selectedProject.id === 'haedal') {
        // Haedal 支持批量查询
        const batchResults = await queryHaedal(addressList);
        addressList.forEach(address => {
          const result = batchResults.get(address);
          if (result) {
            newResults.push({
              address,
              eligible: result.eligible,
              amount: result.amount ? `${result.amount} ${selectedProject.unit}` : undefined,
            });
          } else {
            newResults.push({
              address,
              eligible: false,
              error: '查询失败',
            });
          }
        });
        setResults(newResults);
      } else {
        // 其他项目逐个查询
        for (let i = 0; i < addressList.length; i++) {
          const address = addressList[i];
          
          try {
            let result: ProjectQueryResult;
            
            switch (selectedProject.id) {
              case 'spark':
                result = await querySpark(address);
                break;
              case 'jager':
                result = await queryJager(address);
                break;
              case 'stakestone':
                result = await queryStakestone(address);
                break;
              case 'zora':
                result = await queryZora(address);
                break;
              default:
                throw new Error('未支持的项目');
            }
            
            newResults.push({
              address,
              eligible: result.eligible,
              amount: result.amount ? `${result.amount} ${selectedProject.unit}` : undefined,
            });
            
          } catch (error) {
            console.error(`Query error for ${address}:`, error);
            newResults.push({
              address,
              eligible: false,
              error: error instanceof Error ? error.message : '网络错误',
            });
          }

          // 更新结果显示
          setResults([...newResults]);

          // 如果不是最后一个地址，则等待延迟间隔
          if (i < addressList.length - 1) {
            await sleep(queryDelay * 1000);
          }
        }
      }

      const eligibleCount = newResults.filter(r => r.eligible).length;
      message.success(`查询完成！共 ${addressList.length} 个地址，${eligibleCount} 个符合条件`);
      
    } catch (error) {
      console.error('Batch query error:', error);
      message.error('批量查询失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 计算总空投数量
  const calculateTotalAmount = (results: QueryResult[]): string => {
    const eligibleResults = results.filter(r => r.eligible && r.amount);
    
    if (eligibleResults.length === 0) {
      return '0';
    }

    try {
      let total = 0;
      
      for (const result of eligibleResults) {
        const numericValue = getAmountValue(result.amount);
        // 移除千位分隔符然后解析
        const cleanValue = numericValue.replace(/,/g, '');
        const parsed = parseFloat(cleanValue);
        if (!isNaN(parsed)) {
          total += parsed;
        }
      }
      
      return total.toLocaleString('en-US', { 
        minimumFractionDigits: 0,
        maximumFractionDigits: 4 
      });
    } catch (error) {
      console.error('Calculate total amount error:', error);
      return '0';
    }
  };

  // 获取项目单位
  const getProjectUnit = (): string => {
    return selectedProject?.unit || '';
  };

  // 从amount字符串中提取数值部分
  const getAmountValue = (amount?: string): string => {
    if (!amount) return '0';
    
    // 如果amount包含单位，提取数值部分
    const match = amount.match(/^([\d,.]+)/);
    if (match) {
      return match[1];
    }
    
    return amount;
  };

  if (!selectedProject) {
    return (
      <div style={{ padding: '24px' }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <Title level={2}>空投/白名单中奖查询</Title>
          <Paragraph type="secondary">项目列表</Paragraph>
        </div>

        <Row gutter={[16, 16]}>
          {airdropProjects.map((project) => (
            <Col xs={24} sm={12} lg={8} xl={6} key={project.id}>
              <Card
                hoverable
                style={{ height: '100%' }}
                bodyStyle={{ padding: '16px' }}
                onClick={() => handleProjectSelect(project)}
              >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                  <Avatar 
                    size={40} 
                    src={project.icon}
                    style={{ marginRight: '12px' }}
                  />
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      {project.name} 空投钱包批量查询
                    </Title>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      截止时间: {project.status}
                    </Text>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Tag color="orange">免费</Tag>
                  <Button type="primary" size="small">
                    进入
                  </Button>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Button onClick={handleBackToList} style={{ marginBottom: '16px' }}>
          ← 返回
        </Button>
        
        <Title level={2} style={{ margin: 0 }}>
          {selectedProject.name} 空投钱包批量查询
        </Title>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={8}>
          

          <Card title="最近热门项目">
            <List
              size="small"
              dataSource={airdropProjects.filter(p => p.id !== selectedProject.id).slice(0, 8)}
              renderItem={(item) => (
                <List.Item 
                  style={{ cursor: 'pointer', padding: '8px 0' }}
                  onClick={() => handleProjectSelect(item)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                    <Avatar size={24} src={item.icon} style={{ marginRight: '8px' }} />
                    <div style={{ flex: 1 }}>
                      <Text style={{ fontSize: '12px' }}>{item.name} 空投钱包批量查询</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: '10px' }}>
                        截止时间: {item.status}
                      </Text>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Card>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
              <Avatar 
                size={48} 
                src={selectedProject.icon}
                style={{ marginRight: '16px' }}
              />
              <div>
                <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
                  {selectedProject.name} 空投钱包批量查询
                </Title>
                <div style={{ marginBottom: '4px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {selectedProject.name} Airdrop eligibility Checker 官网查询地址: 
                  </Text>
                  {selectedProject.description.includes('https') && (
                    <Button 
                      type="link" 
                      size="small"
                      style={{ 
                        padding: '0 4px', 
                        fontSize: '12px', 
                        height: 'auto',
                        color: '#1890ff'
                      }}
                      onClick={() => {
                        const url = selectedProject.description.split('官网查询地址: ')[1];
                        if (url) {
                          window.open(url, '_blank');
                        }
                      }}
                    >
                      {selectedProject.description.split('官网查询地址: ')[1]}
                    </Button>
                  )}
                </div>
                <Text type="secondary" style={{ fontSize: '11px', color: '#999' }}>
                  {selectedProject.description.split('官网查询地址:')[0].trim()}
                </Text>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <Tag color="green" icon={<ClockCircleOutlined />}>
                截止时间: {selectedProject.status}
              </Tag>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <Text style={{ fontSize: '16px', fontWeight: 500 }}>
                输入你的钱包地址查询是否在中奖
              </Text>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <Row gutter={16} align="middle">
                <Col span={12}>
                  <Text type="secondary">查询延迟间隔 (秒):</Text>
                  <InputNumber
                    min={1}
                    max={10}
                    value={queryDelay}
                    onChange={(value) => setQueryDelay(value || 1)}
                    style={{ marginLeft: '8px', width: '80px' }}
                    size="small"
                  />
                </Col>
                <Col span={12}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    设置每个地址查询之间的延迟时间，避免请求过快
                  </Text>
                </Col>
              </Row>
            </div>

            <TextArea
              placeholder="请输入钱包地址，多个钱包地址请换行"
              value={addresses}
              onChange={(e) => setAddresses(e.target.value)}
              rows={8}
              style={{ marginBottom: '16px' }}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <Text type="secondary">查询 (秒): {queryTime}</Text>
              <Button 
                type="primary" 
                size="large"
                loading={loading}
                onClick={queryAirdrop}
                icon={<SearchOutlined />}
              >
                立即查询
              </Button>
            </div>

            {results.length > 0 && (
              <Card title="查询结果" style={{ marginTop: '24px' }}>
                <Row gutter={16} style={{ marginBottom: '16px' }}>
                  <Col span={6}>
                    <div style={{ fontSize: '12px' }}>
                      <Statistic 
                        title="总查询数量" 
                        value={results.length} 
                        prefix={<SearchOutlined />}
                        valueStyle={{ fontSize: '16px' }}
                      />
                    </div>
                  </Col>
                  <Col span={6}>
                    <div style={{ fontSize: '12px' }}>
                      <Statistic 
                        title="中奖数量" 
                        value={results.filter(r => r.eligible).length} 
                        prefix={<CheckCircleOutlined />}
                        valueStyle={{ color: '#3f8600', fontSize: '16px' }}
                      />
                    </div>
                  </Col>
                  <Col span={6}>
                    <div style={{ fontSize: '12px' }}>
                      <Statistic 
                        title="未中奖数量" 
                        value={results.filter(r => !r.eligible).length} 
                        prefix={<CloseCircleOutlined />}
                        valueStyle={{ color: '#cf1322', fontSize: '16px' }}
                      />
                    </div>
                  </Col>
                  <Col span={6}>
                    <div style={{ fontSize: '12px' }}>
                      <Statistic 
                        title="可领总数" 
                        value={calculateTotalAmount(results)} 
                        valueStyle={{ color: '#3f8600', fontSize: '16px' }}
                        suffix={getProjectUnit()}
                      />
                    </div>
                  </Col>
                </Row>

                <List
                  dataSource={results}
                  renderItem={(item) => (
                    <List.Item
                      style={{
                        backgroundColor: item.eligible ? '#f6ffed' : '#fafafa',
                        border: `1px solid ${item.eligible ? '#b7eb8f' : '#d9d9d9'}`,
                        borderRadius: '6px',
                        marginBottom: '8px',
                        padding: '12px 16px'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                          <Text 
                            code 
                            copyable={{ text: item.address }}
                            style={{ 
                              fontFamily: 'Monaco, Consolas, monospace',
                              fontSize: '13px',
                              margin: 0,
                              marginRight: '16px'
                            }}
                          >
                            {item.address}
                          </Text>
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {item.eligible ? (
                            <>
                              <Text style={{ 
                                color: '#3f8600', 
                                fontWeight: 'bold',
                                fontSize: '14px'
                              }}>
                                可领: {getAmountValue(item.amount)}
                              </Text>
                              <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                            </>
                          ) : (
                            <>
                              <Text style={{ 
                                color: '#8c8c8c',
                                fontSize: '14px'
                              }}>
                                {item.error || '未中奖'}
                              </Text>
                              <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />
                            </>
                          )}
                        </div>
                      </div>
                    </List.Item>
                  )}
                />
              </Card>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AirdropWhitelist; 